export type WidgetCorner = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'

export const DEFAULT_CORNER: WidgetCorner = 'bottom-right'
export const STORAGE_KEY = 'ai-widget-corner'
export const DRAG_THRESHOLD_PX = 8
export const CORNER_INSET = 20
export const BUTTON_SIZE = 60
export const CONTAINER_Z_INDEX = 1300
export const DIALOG_EDGE_OFFSET = 36
export const DIALOG_SIDE_SHIFT = 580
export const DIALOG_SIDE_SHIFT_EXPANDED = 950
export const DIALOG_LEFT_GAP = 30

const CORNERS: WidgetCorner[] = ['top-left', 'top-right', 'bottom-left', 'bottom-right']

export interface Point {
  x: number
  y: number
}

export interface ButtonRect {
  left: number
  right: number
  top: number
  bottom: number
  width: number
  height: number
}

export interface DialogPaperPosition {
  top: number | 'auto'
  right: 'auto'
  bottom: number | 'auto'
  left: number
}

export interface CornerStorage {
  getItem(key: string): string | null
  setItem?(key: string, value: string): void
}

export const getNearestCorner = (
  x: number,
  y: number,
  viewportWidth: number,
  viewportHeight: number,
): WidgetCorner => {
  const isLeft = x < viewportWidth / 2
  const isTop = y < viewportHeight / 2

  if (isTop && isLeft) {
    return 'top-left'
  }
  if (isTop) {
    return 'top-right'
  }
  if (isLeft) {
    return 'bottom-left'
  }
  return 'bottom-right'
}

export const hasExceededDragThreshold = (
  start: Point,
  current: Point,
  threshold = DRAG_THRESHOLD_PX,
): boolean => {
  const dx = current.x - start.x
  const dy = current.y - start.y
  return dx * dx + dy * dy >= threshold * threshold
}

const isWidgetCorner = (value: string | null): value is WidgetCorner =>
  CORNERS.includes(value as WidgetCorner)

export const readStoredCorner = (storage: CornerStorage): WidgetCorner => {
  const value = storage.getItem(STORAGE_KEY)
  return isWidgetCorner(value) ? value : DEFAULT_CORNER
}

export const writeStoredCorner = (corner: WidgetCorner, storage: CornerStorage): void => {
  storage.setItem?.(STORAGE_KEY, corner)
}

export const getContainerStyle = (
  corner: WidgetCorner,
  dragPosition: Point | null,
): Record<string, string | number> => {
  if (dragPosition) {
    return {
      position: 'fixed',
      zIndex: CONTAINER_Z_INDEX,
      left: dragPosition.x,
      top: dragPosition.y,
    }
  }

  const isTop = corner === 'top-left' || corner === 'top-right'
  const isLeft = corner === 'top-left' || corner === 'bottom-left'

  return {
    position: 'fixed',
    zIndex: CONTAINER_Z_INDEX,
    top: isTop ? CORNER_INSET : 'auto',
    right: isLeft ? 'auto' : CORNER_INSET,
    bottom: isTop ? 'auto' : CORNER_INSET,
    left: isLeft ? CORNER_INSET : 'auto',
  }
}

export const getDialogPaperPosition = (
  corner: WidgetCorner,
  button: Pick<ButtonRect, 'left' | 'right'> | undefined,
  expanded: boolean,
): DialogPaperPosition => {
  const sideShift = expanded ? DIALOG_SIDE_SHIFT_EXPANDED : DIALOG_SIDE_SHIFT
  const isTop = corner === 'top-left' || corner === 'top-right'
  const isLeft = corner === 'top-left' || corner === 'bottom-left'
  const fallbackLeft = isLeft ? DIALOG_LEFT_GAP : 0
  const left = button
    ? isLeft
      ? button.right + DIALOG_LEFT_GAP
      : button.left - sideShift
    : fallbackLeft

  return {
    top: isTop ? DIALOG_EDGE_OFFSET : 'auto',
    right: 'auto',
    bottom: isTop ? 'auto' : DIALOG_EDGE_OFFSET,
    left,
  }
}
